import {chain, difference, first, isUndefined, last, range, uniq, without} from 'lodash';

import {hasMetaKey} from '../../libs/shared/helpers/src/browser/keyboardHelpers';
import {FrontClickEvent, FrontKeyboardEvent} from '../../libs/shared/helpers/src/react/reactHelpers';
import {ItemIndexes} from '../../srcCommon/helpers/itemIndexHelpers';
import {
  allExceptInboxesSelection,
  computeIsAllExceptSelection,
  emptyInboxesSelection,
  InboxesSelection,
  normalInboxesSelection,
} from '../store/panels/inboxes/parameters/inboxesParametersHelpers';

/** Compute the new array of selected IDs after a click on a cell.
 *
 * Note: This function currently relies on the order of the selectedIds
 * when computing the selected range on a shift+click selection. If you
 * re-order the selected IDS the selection will break.
 *
 */
export function getSelectedIdsOnClick<T>(
  selectedIds: ReadonlyArray<T>,
  itemIds: ReadonlyArray<T>,
  itemIndexes: ItemIndexes,
  clickedItemId: T,
  e: FrontClickEvent,
) {
  // If the meta key is pressed, only toggle the specified cell.
  if (hasMetaKey(e)) {
    return toggleSelectedId(selectedIds, clickedItemId);
  }

  // If the shift key is pressed, update the selected range of cells.
  if (e.shiftKey) {
    return toggleSelectedConversationIdRange(selectedIds, itemIds, itemIndexes, clickedItemId);
  }

  // If there was no modifiers, replace the selection.
  return [clickedItemId];
}

/** Add or remove a conversation ID from the list of selected IDs. */
function toggleSelectedId<T>(selectedIds: ReadonlyArray<T>, toggleId: T) {
  const isSelected = selectedIds.includes(toggleId);

  // Don't unselect the last item.
  if (isSelected && selectedIds.length === 1) {
    return selectedIds;
  }

  // Unselect the specified item.
  if (isSelected) {
    return without(selectedIds, toggleId);
  }

  // Select the specified item.
  return [toggleId, ...uniq(selectedIds)];
}

/** Add or remove a range of IDs from the list of selected IDs. */
function toggleSelectedConversationIdRange<T>(
  selectedIds: ReadonlyArray<T>,
  itemIds: ReadonlyArray<T>,
  itemIndexes: ItemIndexes,
  endId: T,
) {
  // The last ID is used as reference to build the range.
  // If it doesn't exist, just select the conversation that was clicked.
  const startId = last(selectedIds);
  if (!startId || startId === endId) {
    return [endId];
  }

  // Find the indexes of our start and end points.
  const endIndex = itemIndexes[String(endId)];
  const startIndex = itemIndexes[String(startId)];
  if (endIndex < 0 || startIndex < 0 || endIndex === startIndex) {
    return selectedIds;
  }

  // We will act on an array of indexes going forward.
  const isGoingDown = endIndex > startIndex;
  const selectedIndexes = chain(selectedIds)
    .map((id) => itemIndexes[String(id)])
    .filter((id) => id >= 0)
    .value();

  // Remove the indexes in contiguous selected groups around our new selection range.
  const selectedIndexesWithoutContiguous = selectedIndexes.filter((index) => {
    // If it's "before" the start index, remove contiguous selected indexes.
    if ((isGoingDown && index < startIndex) || (!isGoingDown && index > startIndex)) {
      return range(index, startIndex).some((i) => !selectedIndexes.includes(i));
    }

    // If it's "after" the end index, remove contiguous selected indexes.
    if ((isGoingDown && index > endIndex) || (!isGoingDown && index < endIndex)) {
      return range(index, endIndex).some((i) => !selectedIndexes.includes(i));
    }

    return index !== endIndex;
  });

  // Find all the indexes to add between end and start.
  const indexesToAdd = difference(range(endIndex, startIndex), selectedIndexesWithoutContiguous);

  // Combine the new indexes with the old ones.
  const newSelectedIndexes = [...indexesToAdd, ...selectedIndexesWithoutContiguous];
  return newSelectedIndexes.map((i) => itemIds[i]);
}

/** Compute the new array of selected IDs after an arrow up/down keypress. */
export function getSelectedIdsOnKey<T>(
  selectedIds: ReadonlyArray<T>,
  itemIds: ReadonlyArray<T>,
  itemIndexes: ItemIndexes,
  e: FrontKeyboardEvent,
  isBefore: boolean,
) {
  const offset = isBefore ? -1 : 1;

  // If Shift wasn't pressed, just move the selection.
  if (!e.shiftKey) {
    return moveSelectedIdByOffset(selectedIds, itemIds, itemIndexes, offset);
  }

  // Otherwise, extend the selection in the specified direction.
  return expandSelectedIdRangeByOffset(selectedIds, itemIds, itemIndexes, offset);
}

/** Move the current selection by the provided offset. */
function moveSelectedIdByOffset<T>(
  selectedIds: ReadonlyArray<T>,
  itemIds: ReadonlyArray<T>,
  itemIndexes: ItemIndexes,
  offset: number,
) {
  // Use the first selected ID as the reference.
  const referenceId = first(selectedIds);
  if (!referenceId) {
    return selectedIds;
  }

  // Find the new conversation to add.
  const referenceIndex = itemIndexes[String(referenceId)];
  const newItemId = itemIds[referenceIndex + offset];
  if (isUndefined(newItemId)) {
    return selectedIds;
  }

  return [newItemId];
}

/** Expand the current selection range by the provided offset. */
function expandSelectedIdRangeByOffset<T>(
  selectedIds: ReadonlyArray<T>,
  itemIds: ReadonlyArray<T>,
  itemIndexes: ItemIndexes,
  offset: number,
) {
  // Find the indexes of all the selected IDs.
  const selectedIndexes = chain(selectedIds)
    .map((id) => itemIndexes[String(id)])
    .filter((id) => id >= 0)
    .value();

  // Use the first index as reference.
  const referenceIndex = first(selectedIndexes);
  if (isUndefined(referenceIndex)) {
    return selectedIds;
  }

  // Check if the next index is within bounds.
  const nextIndex = referenceIndex + offset;
  if (isUndefined(itemIds[nextIndex])) {
    return selectedIds;
  }

  // Check if the next item in the opposite selection is selected.
  const isNextSelected = selectedIndexes.includes(nextIndex);
  const isPreviousSelected = selectedIndexes.includes(referenceIndex - offset);

  // Remove the current and new indexes from the selected.
  const staticSelectedIndexes = without(selectedIndexes, referenceIndex, nextIndex);

  // Build the new array of selected indexes.
  const newSelectedIndex =
    isNextSelected && !isPreviousSelected
      ? [nextIndex, ...staticSelectedIndexes]
      : [nextIndex, referenceIndex, ...staticSelectedIndexes];

  // Map to IDs and return.
  return newSelectedIndex.map((i) => itemIds[i]);
}

/** Compute the new inboxes selection after a click on a cell. */
export function getSelectionOnClick<T>(
  selection: InboxesSelection<T>,
  itemIds: ReadonlyArray<T>,
  itemIndexes: ItemIndexes,
  clickedItemId: T,
  e: FrontClickEvent,
) {
  // If the meta key is pressed, only toggle the specified cell.
  if (hasMetaKey(e)) {
    return toggleSelection(selection, clickedItemId);
  }

  // If the shift key is pressed, update the selected range of cells.
  if (e.shiftKey) {
    return toggleSelectionIdRange(selection, itemIds, itemIndexes, clickedItemId);
  }

  // If there was no modifiers, replace the selection.
  return normalInboxesSelection([clickedItemId]);
}

/** Toggles an ID in the selected or deselected IDs of an inboxes selection */
function toggleSelection<T>(selection: InboxesSelection<T>, toggleId: T) {
  if (computeIsAllExceptSelection(selection)) {
    if (selection.deselectedIds.includes(toggleId)) {
      return allExceptInboxesSelection(without(selection.deselectedIds, toggleId));
    }

    return allExceptInboxesSelection([toggleId, ...uniq(selection.deselectedIds)]);
  }

  return normalInboxesSelection(toggleSelectedId(selection.selectedIds, toggleId));
}

/** Add or remove a range of IDs from an inboxes selection */
function toggleSelectionIdRange<T>(
  selection: InboxesSelection<T>,
  itemIds: ReadonlyArray<T>,
  itemIndexes: ItemIndexes,
  endId: T,
) {
  if (computeIsAllExceptSelection(selection)) {
    const deselectedIds = toggleSelectedConversationIdRange(
      selection.deselectedIds,
      itemIds,
      itemIndexes,
      endId,
    );
    return allExceptInboxesSelection(deselectedIds);
  }

  const selectedIds = toggleSelectedConversationIdRange(selection.selectedIds, itemIds, itemIndexes, endId);
  return normalInboxesSelection(selectedIds);
}

/**
 * Compute the new inboxes selection after an arrow up/down keypress. Note that
 * if we are currently in an all except selection, this will return to a normal inboxes
 * selection with the first itme selected
 */
export function getSelectionOnKey<T>(
  selection: InboxesSelection<T>,
  itemIds: ReadonlyArray<T>,
  itemIndexes: ItemIndexes,
  e: FrontKeyboardEvent,
  isBefore: boolean,
) {
  if (computeIsAllExceptSelection(selection)) {
    // Returning the empty inboxes selection here defaults us back to just
    // having the first item selected in a normal selection
    return emptyInboxesSelection;
  }

  return normalInboxesSelection(
    getSelectedIdsOnKey(selection.selectedIds, itemIds, itemIndexes, e, isBefore),
  );
}
